import { useState } from 'react';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import FormTextField from 'components/IAP/formFields/FormTextField';
import { FormProvider } from 'react-hook-form';
import {
  ResourceRequest,
  ResourceRequestItem,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import { Row } from 'react-bootstrap';
import { ModalSkeleton } from './ModalSkeleton';
import { StylishSwitcher } from 'components/DesignSystems/New/StylishSwitcher';
import DocViewer from '@cyntler/react-doc-viewer';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';

type ApprovalModalProps = {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  request: ResourceRequest;
  currentStep: number;
  modalStep: number;
  handleApprove: (items?: ResourceRequestItem[][]) => Promise<void>;
  handleDeny: () => void;
  form: any;
  memberPositions: { positionId: string; positionName: string }[];
  logisticsItems: ResourceRequestItem[][];
};

export const ApprovalModal = ({
  showModal,
  setShowModal,
  currentStep,
  modalStep,
  request,
  handleApprove,
  handleDeny,
  form,
  memberPositions,
  logisticsItems,
}: ApprovalModalProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const approved = modalStep < currentStep || request.status === 'completed';
  const documents = [{ uri: 'fileUrl', fileName: 'Work in progress' }];
  const icsFormPdf = useOptionalIcsFormPdfPreview();
  const exportAsForm = icsFormPdf?.exportAsForm;

  const onSubmit = async () => {
    if (modalStep === 4) {
      await handleApprove(logisticsItems);
    } else {
      await handleApprove();
    }
    setShowModal(false);
  };
  const getString = (modalStep: number) => {
    switch (modalStep) {
      case 1:
        return 'requestor';
      case 2:
        return 'section_chief';
      case 3:
        return 'resl';
      case 4:
        return 'logistics';
      case 5:
        return 'finance';
      case 6:
        return 'final';
      case 7:
        return 'incident_commander';
      default:
        return '';
    }
  };

  const handleExport = () => {
    void exportAsForm?.({
      formname: 'ICS_213RR_CG',
      data: { requestId: request.resource_request_id },
      mapSketch: null,
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <ModalSkeleton
          show={showModal}
          heading="Approval"
          onClose={() => {
            setShowModal(false);
          }}
          onDeny={() => {
            handleDeny();
            setShowModal(false);
          }}
          onConfirm={() => {
            form.handleSubmit(onSubmit)();
          }}
          viewOnly={approved}
        >
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <div className="d-flex align-items-center gap-2">
              <span>ICS-213RR Preview</span>
              <StylishSwitcher
                className=""
                checked={showPreview}
                onChange={() => setShowPreview(!showPreview)}
                value={showPreview}
              />
            </div>
            <StylishNewButton
              className="button--secondary"
              onClick={handleExport}
            >
              Preview ICS-213RR
            </StylishNewButton>
          </div>

          <div className="bg-neutral-700 p-2 border rounded">
            <div className="mb-2">
              You are approving all resources in this request:
            </div>
            {request?.items &&
              request?.items.map((item: ResourceRequestItem, i: number) => (
                <ul key={i}>
                  Request {i + 1}: {item.name}
                </ul>
              ))}
          </div>
          {showPreview ? (
            <DocViewer documents={documents} />
          ) : (
            <ModalContent
              viewOnly={approved}
              memberPositions={memberPositions}
              keyString={getString(modalStep)}
            />
          )}
        </ModalSkeleton>
      </form>
    </FormProvider>
  );
};

const ModalContent = ({
  viewOnly,
  memberPositions,
  keyString,
}: {
  viewOnly: boolean;
  memberPositions: { positionId: string; positionName: string }[];
  keyString: string;
}) => {
  return (
    <div>
      <Row>
        <div className="col-6">
          <FormTextField
            title="Name*"
            fieldName={`${keyString}_name`}
            disabled={viewOnly}
            rules={{ required: 'Name is required' }}
          />
        </div>
        <div className="col-6">
          {viewOnly ? (
            <FormTextField
              title="Positions"
              fieldName={`${keyString}_position`}
              disabled={viewOnly}
            />
          ) : (
            <div className="mt-2">
              Positions:
              <div className="mt-3">
                {memberPositions?.map((pos) => pos.positionName).join(', ')}
              </div>
            </div>
          )}
        </div>
      </Row>
      {keyString !== 'requestor' && (
        <Row>
          <FormTextAreaField
            title="Comments"
            fieldName={`${keyString}_comments`}
            disabled={viewOnly}
          />
        </Row>
      )}
    </div>
  );
};
